import React, { useEffect, useState, useCallback, memo } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { viewBuilderResale } from "../../Redux/Actions/builderResaleAction";
import toast from "react-hot-toast";
import MainLayout from "../../Layout/mainLayout";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "./viewBuilder.css";

const ViewBuilderResale = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const [builderData, setBuilderData] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);

  const fetchBuilderDetails = useCallback(async () => {
    try {
      setPageLoading(true);
      const res = await dispatch(viewBuilderResale({ builder_id: id }));

      if (res?.statusCode === 200) {
        setBuilderData(res.data);
      } else {
        toast.error(res?.message || "Failed to load builder details");
      }
    } catch (error) {
      toast.error("Error fetching builder details");
    } finally {
      setPageLoading(false);
    }
  }, [dispatch, id]);

  useEffect(() => {
    fetchBuilderDetails();
  }, [fetchBuilderDetails]);

  if (!builderData && !pageLoading) {
    return (
      <MainLayout>
        <div className="vbr-empty-wrap">
          <div className="vbr-empty-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
            </svg>
          </div>
          <p className="vbr-empty-text">No Builder Data Found.</p>
          <Link to="/builder_resale" className="vbr-back-link">← Back to Builders</Link>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="vbr-wrapper">

        {/* HEADER */}
        <div className="vbr-header">
          <div className="vbr-header-left">
            <Link to="/builder_resale" className="vbr-back-btn">Back</Link>
            <div className="vbr-header-divider" />

            <div>
              <div className="vbr-breadcrumb">Builder Resale / List / View</div>
              <h1 className="vbr-title">
                {pageLoading ? <Skeleton width={200} /> : (builderData?.brand_name || "No Data")}
              </h1>
            </div>
          </div>

          <Link to={`/builder_resale/edit/${id}`} className="vbr-edit-btn">
            Edit Builder
          </Link>
        </div>

        {/* HERO */}
        <div className="vbr-hero-card">
          <div className="vbr-hero-left">

            {pageLoading ? (
              <Skeleton circle width={72} height={72} />
            ) : builderData?.brand_logo ? (
              <div className="vbr-hero-logo-wrap">
                <img
                  src={builderData.brand_logo}
                  alt="Brand logo"
                  className="vbr-logo-preview-img"
                  loading="lazy"
                  width="72"
                  height="72"
                />
              </div>
            ) : (
              <div className="vbr-hero-avatar">
                {builderData?.brand_name?.charAt(0)?.toUpperCase() || "B"}
              </div>
            )}

            <div>
              <span className="vbr-hero-label">Builder Name</span>
              <span className="vbr-hero-name">
                {pageLoading ? <Skeleton width={150} /> : (builderData?.brand_name || "—")}
              </span>

              <span className="vbr-hero-brand">
                {pageLoading ? <Skeleton width={120} /> : builderData?.organization_name}
              </span>
            </div>
          </div>

          <div className="vbr-hero-meta">
            {pageLoading ? (
              <Skeleton width={100} height={30} />
            ) : (
              builderData?.account_status && (
                <div className={`vbr-status-chip vbr-status-chip--${String(builderData.account_status).toLowerCase()}`}>
                  {builderData.account_status}
                </div>
              )
            )}
          </div>
        </div>

        {/* GRID */}
        <div className="vbr-grid-layout">

          {/* LEFT */}
          <div className="vbr-col">
            <div className="vbr-card">
              <div className="vbr-card-header">
                <span className="vbr-card-badge">01</span>
                <h2 className="vbr-card-title">Basic Information</h2>
              </div>

              <div className="vbr-info-grid">
                {["Builder Name", "Organization Name", "Current Status"].map((label, i) => (
                  <div className="vbr-info-item" key={i}>
                    <span className="vbr-info-label">{label}</span>
                    <span className="vbr-info-value">
                      {pageLoading ? <Skeleton width={120} /> : (
                        i === 0 ? builderData?.brand_name :
                          i === 1 ? builderData?.organization_name :
                            builderData?.account_status
                      )}
                    </span>
                  </div>
                ))}

                {/* <div className="vbr-info-item vbr-info-item--full">
                  <span className="vbr-info-label">Brand Logo</span>
                  {pageLoading ? (
                    <Skeleton height={80} width={80} />
                  ) : builderData?.brand_logo ? (
                    <img src={builderData.brand_logo} alt="logo" width={80} />
                  ) : (
                    "No Data"
                  )}
                </div> */}
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div className="vbr-col">
            <div className="vbr-card">
              <div className="vbr-card-header">
                <span className="vbr-card-badge">03</span>
                <h2 className="vbr-card-title">Address Details</h2>
              </div>

              <div className="vbr-info-grid">
                {["Street", "Country", "State", "City"].map((label, i) => (
                  <div className="vbr-info-item" key={i}>
                    <span className="vbr-info-label">{label}</span>
                    <span className="vbr-info-value">
                      {pageLoading ? <Skeleton width={140} /> : (
                        i === 0 ? (builderData?.street_address || "N/A") :
                          i === 1 ? (builderData?.country?.country_name || "N/A") :
                            i === 2 ? (builderData?.state?.state_name || "N/A") :
                              (builderData?.city?.city_name || "N/A")
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </MainLayout>
  );
};

export default memo(ViewBuilderResale);